import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon, Field, Label, ChoiceCard, Toggle, PrimaryButton } from "@/components/kit";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { api, history } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { useProBranding } from "@/hooks/useProBranding";
import { ProFooter, LegalLinks } from "@/components/prospect";

// Indicatifs pays supportés (le préfixe détermine les règles fiscales côté backend).
const PHONE_PREFIXES = [
  { code: "+32", name: "Belgique" },
  { code: "+33", name: "France" },
  { code: "+352", name: "Luxembourg" },
  { code: "+41", name: "Suisse" },
  { code: "+31", name: "Pays-Bas" },
  { code: "+40", name: "Roumanie" },
  { code: "+49", name: "Allemagne" },
];

// Accepte le point ET la virgule pour les quantités/métrés
const num = (v) => Number["parseFloat"](String(v ?? "").replace(/,/g, "."));
const int = (v) => Number["parseInt"](String(v ?? "").replace(/,/g, "."), 10);
const emptyRoom = () => ({
  name: "", floor: "", wall_height: "", wall_length: "", wall_width: "",
  num_doors: "", floor_area: "", windows: [],
  wp_type: "normal", wp_roll_width: "0.53", wp_reference: "",
  paint_quality: "standard", paint_color: "", wall_state: "", finish_type: "",
  floor_covering: "", floor_roll_width: "4", tile_size: "", tile_pack_m2: "",
  product_name: "", product_price: "", weld_reference: "", carpet_pose: "",
  furniture_to_move: false,
  needs_underlay: false, underlay_type: "normale", needs_glue: false,
  needs_hot_weld: false, needs_cold_weld: false,
  needs_floor_removal: false, needs_floor_leveling: false, needs_floor_protection: false,
  needs_skirting: false, needs_counter_skirting: false,
  num_rosettes: "", num_thresholds: "", num_radiators: "",
});

const initial = {
  client_name: "", client_email: "", client_phone: "", phone_prefix: "+32", phone_local: "",
  property_type: "", floor_level: "",
  building_over_10y: "", needs_rails: false, rail_kind: "rail", rail_vat21: false,
  rooms: [emptyRoom()],
  needs_dewallpapering: false, needs_smoothing: false, needs_repairs: false, paint_location: "",
  paint_walls: true, paint_plinthes: false, paint_portes: false, paint_chassis: false, paint_corniches: false,
  needs_tarps: false, needs_scaffolding: false, needs_ladder: false,
  needs_masking: false, floor_paint: false, floor_paint_type: "epoxy", floor_paint_finish: "aqueous",
  floor_paint_area: "", paint_doors_count: "", door_finish: "synthetic",
  needs_nonwoven: false, needs_moldings: false, molding_type: "expansee", molding_action: "pose", molding_meters: "", molding_bars: "",
  needs_lining: false, lining_type: "", voile_lined: false, voile_width: "",
  curtain_kind: "", ruflette_type: "", store_family: "", store_type: "",
  plisse_variant: "", plisse_opaque: false, plisse_thermique: false,
  has_own_material: false, wp_from_ligot: false, wp_sold_by: "roll", wp_price: "",
  wp_type: "normal", wp_reference: "", wp_mode: "only",
  wp_roll_width: "0.53", wp_roll_length: "10.05", wp_repeat_type: "none", wp_repeat: "",
  product_name: "", product_price: "", product_width: "",
  curtain_repeat_type: "none", curtain_repeat: "", fullness: "2", hook_type: "glisser",
  needs_floor_removal: false, needs_floor_leveling: false, needs_floor_protection: false, needs_hot_weld: false, needs_cold_weld: false,
  num_radiators: "",
  floor_type: "", needs_glue: false, needs_underlay: false, needs_skirting: false, weld_reference: "",
  underlay_type: "normale", carpet_pose: "", stairs_straight: "", stairs_winder: "", stairs_landing: "",
  needs_counter_skirting: false, num_rosettes: "", num_thresholds: "",
  notes: "", photos: [], gdpr_consent: false,
};

const GroupLabel = ({ children }) => (
  <p className="mb-2 text-[13px] font-medium text-ink-3">{children}</p>
);
const Hint = ({ children }) => <p className="-mt-1 mb-3 text-[13px] text-mute">{children}</p>;
const Row = ({ children, cols = 2 }) => (
  <div className={cn("mb-4 grid gap-3", cols === 3 ? "grid-cols-3" : "grid-cols-2")}>{children}</div>
);
const InfoBox = ({ children }) => (
  <div className="mb-4 flex items-center gap-2 rounded-xl bg-brand-tertiary px-3 py-2.5 text-[13px] leading-snug text-brand-on">
    <Icon name="info" size={16} className="shrink-0 text-brand" />
    <span>{children}</span>
  </div>
);

// Mix papier peint/peinture : surfaces nettes par finition (Mur1&3 = longueur, Mur2&4 = largeur).
const wallSplit = (room) => {
  const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };
  const H = n(room.wall_height) || 2.5;
  const base = [n(room.wall_length), n(room.wall_width), n(room.wall_length), n(room.wall_width)];
  const walls = room.walls || [];
  const fins = [0, 1, 2, 3].map((i) => (walls[i] === "paint" ? "paint" : "wallpaper"));
  const wpLin = base.reduce((s, b, i) => s + (fins[i] === "wallpaper" ? b : 0), 0);
  const ptLin = base.reduce((s, b, i) => s + (fins[i] === "paint" ? b : 0), 0);
  const tot = wpLin + ptLin;
  const openArea = (n(room.num_doors) * 0.9 * 2.1) + (room.windows || []).reduce((s, w) => s + (n(w.width) || 1.2) * (n(w.height) || 1.4), 0);
  const wpArea = Math.max(wpLin * H - openArea * (tot ? wpLin / tot : 0), 0);
  const ptArea = Math.max(ptLin * H - openArea * (tot ? ptLin / tot : 0), 0);
  return { wpArea: Math.round(wpArea * 10) / 10, ptArea: Math.round(ptArea * 10) / 10 };
};

export default function QuoteWizard() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();
  const { pro } = useProBranding();

  const steps = useMemo(() => {
    const base = ["contact", "property"];
    if (category === "curtains") return [...base, "rooms", "lining", "supply", "photos"];
    if (category === "flooring") return [...base, "rooms", "flooring", "photos"];
    if (category === "wallpaper") return [...base, "rooms", "condition", "supply", "photos"];
    return [...base, "rooms", "condition", "photos"];
  }, [category]);

  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState(() =>
    category === "wallpaper"
      ? { ...initial, needs_dewallpapering: true, needs_repairs: true, needs_smoothing: true }
      : initial
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const step = steps[stepIdx];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRoom = (ri, key, v, extra) =>
    setForm((f) => ({ ...f, rooms: f.rooms.map((r, i) => (i === ri ? { ...r, [key]: v, ...(extra || {}) } : r)) }));
  const addRoom = () => setForm((f) => ({ ...f, rooms: [...f.rooms, emptyRoom()] }));
  const removeRoom = (ri) => setForm((f) => ({ ...f, rooms: f.rooms.filter((_, i) => i !== ri) }));
  const addWindow = (ri) =>
    setForm((f) => ({
      ...f,
      rooms: f.rooms.map((r, i) =>
        i === ri
          ? { ...r, windows: [...r.windows, { width: "", height: "", total_height: "", sill_height: "", want_voilage: false, want_tenture: true, voilage_plombe: false, mount: "cache_rail", n_elements: "2", drop_target: "sol" }] }
          : r
      ),
    }));
  const removeWindow = (ri, wi) =>
    setForm((f) => ({
      ...f,
      rooms: f.rooms.map((r, i) => (i === ri ? { ...r, windows: r.windows.filter((_, j) => j !== wi) } : r)),
    }));
  const setWindow = (ri, wi, key, v) =>
    setForm((f) => ({
      ...f,
      rooms: f.rooms.map((r, i) =>
        i === ri ? { ...r, windows: r.windows.map((w, j) => (j === wi ? { ...w, [key]: v } : w)) } : r
      ),
    }));

  const roomsValid = () => {
    if (category === "flooring") return form.rooms.every((r) => (r.wall_length && r.wall_width) || r.floor_area);
    if (category === "curtains") return form.rooms.some((r) => r.windows.some((w) => w.width));
    return form.rooms.every((r) => r.wall_height && r.wall_length && r.wall_width);
  };

  const canProceed = () => {
    if (step === "contact") return form.client_name.trim() && form.client_email.includes("@") && form.phone_prefix;
    if (step === "property") return form.property_type && form.floor_level && (category === "curtains" || form.building_over_10y !== "");
    if (step === "rooms") return roomsValid();
    if (step === "photos") return form.gdpr_consent;
    return true;
  };

  const pickPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, photos: [...f.photos, reader.result] }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const next = () => {
    setError("");
    if (!canProceed()) {
      setError(
        step === "photos" && !form.gdpr_consent
          ? t("err_gdpr")
          : step === "rooms"
          ? category === "curtains" ? t("err_curtain_win") : t("err_room_dims")
          : t("err_fields")
      );
      return;
    }
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else submit();
  };

  const back = () => {
    setError("");
    if (stepIdx === 0) navigate(-1);
    else setStepIdx(stepIdx - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const _phoneLocal = (form.phone_local || "").replace(/\D/g, "").replace(/^0+/, "");
      const _composedPhone = form.phone_prefix ? `${form.phone_prefix}${_phoneLocal}` : "";
      const payload = {
        category, lang,
        client_name: form.client_name, client_email: form.client_email, client_phone: _composedPhone,
        property_type: form.property_type, floor_level: form.floor_level,
        building_over_10y: form.building_over_10y === "" ? null : form.building_over_10y === "yes",
        needs_rails: form.needs_rails, rail_kind: form.rail_kind, rail_vat21: form.rail_vat21,
        rooms: form.rooms.map((r) => ({
          name: r.name, floor: r.floor,
          wall_height: num(r.wall_height) || null,
          wall_length: num(r.wall_length) || null,
          wall_width: num(r.wall_width) || null,
          num_doors: int(r.num_doors) || 0,
          floor_area: num(r.floor_area) || null,
          walls: form.wp_mode === "mix" ? [0, 1, 2, 3].map((k) => ((r.walls || [])[k] === "paint" ? "paint" : "wallpaper")) : null,
          wp_type: r.wp_type || "normal",
          wp_roll_width: num(r.wp_roll_width) || null,
          wp_reference: r.wp_reference || "",
          paint_quality: r.paint_quality || "standard",
          paint_color: r.paint_color || "",
          wall_state: r.wall_state || "",
          finish_type: r.wall_state === "stains" ? (r.finish_type || "non_lavable") : "",
          floor_covering: r.floor_covering || null,
          floor_roll_width: r.floor_covering ? (num(r.floor_roll_width) || null) : null,
          tile_size: r.tile_size || "",
          tile_pack_m2: num(r.tile_pack_m2 || "") || null,
          product_name: r.product_name || "",
          product_price: num(r.product_price || "") || null,
          weld_reference: r.weld_reference || "",
          carpet_pose: r.carpet_pose || "",
          furniture_to_move: !!r.furniture_to_move,
          needs_underlay: !!r.needs_underlay,
          underlay_type: r.underlay_type || "normale",
          needs_glue: !!r.needs_glue,
          needs_hot_weld: !!r.needs_hot_weld,
          needs_cold_weld: !!r.needs_cold_weld,
          needs_floor_removal: !!r.needs_floor_removal,
          needs_floor_leveling: !!r.needs_floor_leveling,
          needs_floor_protection: !!r.needs_floor_protection,
          needs_skirting: !!r.needs_skirting,
          needs_counter_skirting: !!r.needs_counter_skirting,
          num_rosettes: int(r.num_rosettes || "") || 0,
          num_thresholds: int(r.num_thresholds || "") || 0,
          num_radiators: int(r.num_radiators || "") || 0,
          windows: r.windows.filter((w) => w.width || w.height).map((w) => ({
            width: num(w.width) || null, height: num(w.height) || null,
            total_height: num(w.total_height || "") || null,
            sill_height: num(w.sill_height || "") || null,
            want_voilage: !!w.want_voilage, want_tenture: !!w.want_tenture,
            voilage_plombe: !!w.voilage_plombe, mount: w.mount || "",
            n_elements: int(w.n_elements || "2") || 2,
            drop_target: w.drop_target || "sol",
            wall_support: w.wall_support || "",
            ceiling_to_rod: num(w.ceiling_to_rod || "") || null,
          })),
        })),
        needs_dewallpapering: form.needs_dewallpapering, needs_smoothing: form.needs_smoothing, needs_repairs: form.needs_repairs,
        paint_location: form.paint_location, paint_walls: form.paint_walls, paint_plinthes: form.paint_plinthes,
        paint_portes: form.paint_portes, paint_chassis: form.paint_chassis, paint_corniches: form.paint_corniches,
        needs_tarps: form.needs_tarps, needs_masking: form.needs_masking,
        floor_paint_area: form.floor_paint ? num(form.floor_paint_area) || null : null,
        floor_paint_type: form.floor_paint ? form.floor_paint_type : "",
        floor_paint_finish: form.floor_paint ? form.floor_paint_finish : "",
        paint_doors_count: int(form.paint_doors_count) || 0, door_finish: form.door_finish,
        needs_scaffolding: form.needs_scaffolding, needs_ladder: form.needs_ladder,
        needs_nonwoven: form.needs_nonwoven, needs_moldings: form.needs_moldings,
        molding_type: form.molding_type, molding_action: form.molding_action || "pose",
        molding_bars: num(form.molding_bars) || null, molding_meters: (num(form.molding_bars) || 0) ? (num(form.molding_bars) * 2) : null,
        needs_lining: form.needs_lining, lining_type: form.lining_type,
        voile_lined: form.voile_lined, voile_width: num(form.voile_width) || null,
        curtain_kind: form.curtain_kind, ruflette_type: form.ruflette_type,
        store_family: form.store_family, store_type: form.store_type,
        plisse_variant: form.plisse_variant, plisse_opaque: form.plisse_opaque, plisse_thermique: form.plisse_thermique,
        has_own_material: form.has_own_material, wp_from_ligot: form.wp_from_ligot,
        wp_sold_by: form.wp_sold_by, wp_type: form.wp_type, wp_reference: form.wp_reference,
        wp_price: num(form.wp_price) || null,
        wp_roll_width: num(form.wp_roll_width) || null,
        wp_roll_length: num(form.wp_roll_length) || null,
        wp_repeat_type: form.wp_repeat_type, wp_repeat: num(form.wp_repeat) || null,
        wp_mode: form.wp_mode || "only",
        product_name: form.product_name, product_price: num(form.product_price) || null,
        product_width: num(form.product_width) || null,
        curtain_repeat_type: form.curtain_repeat_type, curtain_repeat: num(form.curtain_repeat) || null,
        fullness: num(form.fullness) || 2, hook_type: form.hook_type,
        needs_floor_removal: form.needs_floor_removal, needs_floor_leveling: form.needs_floor_leveling,
        needs_floor_protection: form.needs_floor_protection, needs_hot_weld: form.needs_hot_weld, needs_cold_weld: form.needs_cold_weld,
        num_radiators: int(form.num_radiators) || 0,
        floor_type: form.floor_type, weld_reference: form.weld_reference, needs_glue: form.needs_glue, needs_underlay: form.needs_underlay,
        underlay_type: form.underlay_type || "normale", carpet_pose: form.carpet_pose || "",
        stairs_straight: int(form.stairs_straight) || 0, stairs_winder: int(form.stairs_winder) || 0, stairs_landing: int(form.stairs_landing) || 0,
        needs_skirting: form.needs_skirting, needs_counter_skirting: form.needs_counter_skirting,
        num_rosettes: int(form.num_rosettes) || 0, num_thresholds: int(form.num_thresholds) || 0,
        notes: form.notes, photos: form.photos, gdpr_consent: form.gdpr_consent,
      };
      const quote = await api.createQuote(payload);
      history.add({ id: quote.id, category: quote.category, client_name: quote.client_name, created_at: quote.created_at });
      navigate(`/summary/${quote.id}`, { replace: true });
    } catch (e) {
      setError(e.message || t("err_fields"));
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="flex min-h-screen flex-col bg-paper">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-paper/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 pb-3 pt-4">
          <button data-testid="wizard-back" onClick={back} className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white transition-colors hover:bg-paper-3">
            <Icon name="arrow-left" size={20} className="text-ink" />
          </button>
          <div className="flex-1">
            <p className="text-[15px] font-semibold text-ink">{t(`cat_${category}`)}</p>
            <p className="text-xs text-mute">{t("step")} {stepIdx + 1} {t("of")} {steps.length}</p>
          </div>
          <LanguageSwitcher />
        </div>
        <div className="mx-auto max-w-2xl px-5">
          <div className="h-[3px] w-full rounded bg-divider">
            <div className="h-[3px] rounded bg-brand transition-all duration-300" style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-2xl flex-1 px-5 py-7">
        {["rooms", "flooring", "condition", "lining"].includes(step) && (
          <div className="mb-5 flex items-center gap-2 rounded-xl border border-brand-tertiary bg-white px-3 py-2.5 text-[13px] text-ink-2">
            <Icon name="alert-circle" size={16} className="shrink-0 text-brand" />
            <span>{t("complete_options_hint")}</span>
          </div>
        )}

        {step === "contact" && (
          <>
            <StepHead title={t("contact_title")} sub={t("contact_sub")} />
            <Field label={t("f_name")} testId="input-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder={t("ph_name")} />
            <Field label={t("f_email")} testId="input-email" type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder={t("ph_email")} />
            <label className="mb-4 block">
              <Label>{t("f_phone")}</Label>
              <div className="flex gap-2">
                <select
                  data-testid="input-phone-prefix"
                  className="field-input w-[150px] shrink-0"
                  value={form.phone_prefix}
                  onChange={(e) => { const pref = e.target.value; set("phone_prefix", pref); if (pref === "+40" && lang !== "ro") setLang("ro"); }}
                >
                  {PHONE_PREFIXES.map((p) => (
                    <option key={p.code} value={p.code}>{`${p.code} (${p.name})`}</option>
                  ))}
                </select>
                <input
                  data-testid="input-phone"
                  className="field-input flex-1"
                  inputMode="numeric"
                  value={form.phone_local}
                  onChange={(e) => set("phone_local", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder={t("ph_phone")}
                />
              </div>
              <p className="mt-1.5 text-xs text-mute" data-testid="phone-prefix-note">{t("phone_prefix_note")}</p>
            </label>
          </>
        )}

        {step === "property" && (
          <>
            <StepHead title={t("prop_title")} sub={t("prop_sub")} />
            <GroupLabel>{t("prop_type")}</GroupLabel>
            <Row>
              <ChoiceCard testId="choice-house" label={t("opt_house")} icon="home" selected={form.property_type === "house"} onClick={() => set("property_type", "house")} />
              <ChoiceCard testId="choice-apartment" label={t("opt_apartment")} icon="grid" selected={form.property_type === "apartment"} onClick={() => set("property_type", "apartment")} />
            </Row>
            <GroupLabel>{t("level")}</GroupLabel>
            <Row>
              <ChoiceCard testId="choice-ground" label={t("opt_ground")} icon="corner-down-right" selected={form.floor_level === "ground"} onClick={() => set("floor_level", "ground")} />
              <ChoiceCard testId="choice-upper" label={t("opt_upper")} icon="corner-right-up" selected={form.floor_level === "upper"} onClick={() => set("floor_level", "upper")} />
            </Row>
            {category !== "curtains" && (
              <>
                <GroupLabel>{t("building_age")}</GroupLabel>
                <Row>
                  <ChoiceCard testId="choice-age-old" label={t("opt_over10")} icon="clock" selected={form.building_over_10y === "yes"} onClick={() => set("building_over_10y", "yes")} />
                  <ChoiceCard testId="choice-age-new" label={t("opt_under10")} icon="zap" selected={form.building_over_10y === "no"} onClick={() => set("building_over_10y", "no")} />
                </Row>
                <Hint>{t("building_age_hint")}</Hint>
              </>
            )}
          </>
        )}

        {step === "rooms" && (
          <>
            <StepHead title={t("rooms_title")} sub={t("rooms_sub")} />
            {category === "wallpaper" && <InfoBox>{t("walls_required_hint")}</InfoBox>}
            {category === "wallpaper" && (
              <div className="mb-4">
                <GroupLabel>{t("wp_mode_title")}</GroupLabel>
                <Row>
                  <ChoiceCard testId="wpmode-only" label={t("opt_wp_only")} icon="file" selected={(form.wp_mode || "only") === "only"} onClick={() => set("wp_mode", "only")} />
                  <ChoiceCard testId="wpmode-mix" label={t("opt_wp_mix")} icon="layers" selected={form.wp_mode === "mix"} onClick={() => set("wp_mode", "mix")} />
                </Row>
                <Hint>{t("wp_mode_hint")}</Hint>
              </div>
            )}
            {form.rooms.map((room, ri) => (
              <div key={ri} data-testid={`room-${ri}`} className="mb-4 rounded-2xl border border-line bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-[15px] font-semibold text-ink">{t("room_label")} {ri + 1}</p>
                  {form.rooms.length > 1 && (
                    <button data-testid={`remove-room-${ri}`} onClick={() => removeRoom(ri)}>
                      <Icon name="trash-2" size={18} className="text-err" />
                    </button>
                  )}
                </div>
                <Row>
                  <Field label={t("room_name")} testId={`input-roomname-${ri}`} value={room.name} onChange={(e) => setRoom(ri, "name", e.target.value)} placeholder={t("ph_room_name")} className="mb-0" />
                  <Field label={t("room_floor")} testId={`input-roomfloor-${ri}`} value={room.floor} onChange={(e) => setRoom(ri, "floor", e.target.value)} placeholder={t("ph_room_floor")} className="mb-0" />
                </Row>

                {category === "flooring" ? (
                  <>
                    <Row>
                      <Field label={t("f_length")} testId={`input-length-${ri}`} value={room.wall_length} onChange={(e) => setRoom(ri, "wall_length", e.target.value)} placeholder="4" />
                      <Field label={t("f_width")} testId={`input-wallwidth-${ri}`} value={room.wall_width} onChange={(e) => setRoom(ri, "wall_width", e.target.value)} placeholder="3" />
                    </Row>
                    <Field label={t("f_floorarea_opt")} testId={`input-floorarea-${ri}`} value={room.floor_area} onChange={(e) => setRoom(ri, "floor_area", e.target.value)} placeholder="20" />
                    <GroupLabel>{t("floor_covering_label")}</GroupLabel>
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      {[["lino", "opt_fc_lino"], ["vinyl_sol", "opt_fc_vinyl_sol"], ["vinyl_homogene", "opt_fc_vinyl_homogene"], ["vinyl_homogene_dalles", "opt_fc_vinyl_homogene_dalles"], ["pvc_les", "opt_fc_pvc_les"], ["tapis", "opt_fc_tapis"], ["tapis_dalles", "opt_fc_tapis_dalles"], ["moquette_dalle", "opt_fc_moquette_dalle"], ["parquet_cloue", "opt_fc_parquet_cloue"], ["parquet_colle", "opt_fc_parquet_colle"], ["parquet_flottant", "opt_fc_parquet_flottant"], ["parquet_rainure", "opt_fc_parquet_rainure"], ["stratifie", "opt_fc_stratifie"], ["plancher", "opt_fc_plancher"]].map(([val, key]) => (
                        <ChoiceCard key={val} testId={`fc-${val}-${ri}`} label={t(key)} icon="grid" selected={room.floor_covering === val} onClick={() => setRoom(ri, "floor_covering", room.floor_covering === val ? "" : val)} />
                      ))}
                    </div>
                    {(room.floor_covering === "vinyl_sol" || room.floor_covering === "tapis") && (
                      <>
                        <GroupLabel>{t("floor_width_label")}</GroupLabel>
                        <Row>
                          <ChoiceCard testId={`fw-4-${ri}`} label={t("opt_fw_4")} icon="minus" selected={String(room.floor_roll_width || "4") === "4"} onClick={() => setRoom(ri, "floor_roll_width", "4")} />
                          <ChoiceCard testId={`fw-5-${ri}`} label={t("opt_fw_5")} icon="minus" selected={String(room.floor_roll_width) === "5"} onClick={() => setRoom(ri, "floor_roll_width", "5")} />
                        </Row>
                      </>
                    )}
                    {["tapis_dalles", "vinyl_homogene_dalles", "moquette_dalle"].includes(room.floor_covering) && (
                      <Row>
                        <Field label={t("tile_size_label")} testId={`input-tilesize-${ri}`} value={room.tile_size || ""} onChange={(e) => setRoom(ri, "tile_size", e.target.value)} placeholder="50x50" className="mb-0" />
                        <Field label={t("tile_pack_label")} testId={`input-tilepack-${ri}`} value={room.tile_pack_m2 || ""} onChange={(e) => setRoom(ri, "tile_pack_m2", e.target.value)} placeholder="5" className="mb-0" />
                      </Row>
                    )}
                    {room.floor_covering && (
                      <>
                        {(room.floor_covering === "tapis" || room.floor_covering === "tapis_dalles") && (
                          <>
                            <GroupLabel>{t("carpet_pose_label")}</GroupLabel>
                            <Row cols={3}>
                              <ChoiceCard testId={`carpet-double_face-${ri}`} label={t("opt_carpet_double_face")} icon="copy" selected={room.carpet_pose === "double_face"} onClick={() => setRoom(ri, "carpet_pose", "double_face")} />
                              <ChoiceCard testId={`carpet-collee-${ri}`} label={t("opt_carpet_collee")} icon="droplet" selected={room.carpet_pose === "collee"} onClick={() => setRoom(ri, "carpet_pose", "collee", { needs_glue: false })} />
                              <ChoiceCard testId={`carpet-tendue-${ri}`} label={t("opt_carpet_tendue")} icon="move" selected={room.carpet_pose === "tendue"} onClick={() => setRoom(ri, "carpet_pose", "tendue")} />
                            </Row>
                            <GroupLabel>{t("stairs_label")}</GroupLabel>
                            <Hint>{t("stairs_hint")}</Hint>
                            <Row cols={3}>
                              <Field label={t("f_stairs_straight")} testId="input-stairs-straight" value={form.stairs_straight} onChange={(e) => set("stairs_straight", e.target.value)} placeholder="0" className="mb-0" />
                              <Field label={t("f_stairs_winder")} testId="input-stairs-winder" value={form.stairs_winder} onChange={(e) => set("stairs_winder", e.target.value)} placeholder="0" className="mb-0" />
                              <Field label={t("f_stairs_landing")} testId="input-stairs-landing" value={form.stairs_landing} onChange={(e) => set("stairs_landing", e.target.value)} placeholder="0" className="mb-0" />
                            </Row>
                          </>
                        )}
                        <GroupLabel>{t("floor_acc_label")}</GroupLabel>
                        <Toggle testId={`toggle-underlay-${ri}`} label={t("t_underlay")} value={!!room.needs_underlay} onChange={(v) => setRoom(ri, "needs_underlay", v)} />
                        {room.needs_underlay && room.floor_covering === "stratifie" && (
                          <Row cols={3}>
                            <ChoiceCard testId={`underlay-normale-${ri}`} label={`${t("opt_underlay_normale")} · 3€`} icon="layers" selected={(room.underlay_type || "normale") === "normale"} onClick={() => setRoom(ri, "underlay_type", "normale")} />
                            <ChoiceCard testId={`underlay-moyenne-${ri}`} label={`${t("opt_underlay_moyenne")} · 6€`} icon="layers" selected={room.underlay_type === "moyenne"} onClick={() => setRoom(ri, "underlay_type", "moyenne")} />
                            <ChoiceCard testId={`underlay-isolante-${ri}`} label={`${t("opt_underlay_isolante")} · 9€`} icon="layers" selected={room.underlay_type === "isolante"} onClick={() => setRoom(ri, "underlay_type", "isolante")} />
                          </Row>
                        )}
                        {!(room.floor_covering === "tapis" || room.floor_covering === "tapis_dalles") && (
                          <Toggle testId={`toggle-glue-${ri}`} label={t("t_glue")} value={!!room.needs_glue} onChange={(v) => setRoom(ri, "needs_glue", v)} />
                        )}
                        <Toggle testId={`toggle-skirting-${ri}`} label={t("t_skirting")} value={!!room.needs_skirting} onChange={(v) => setRoom(ri, "needs_skirting", v)} />
                        <Toggle testId={`toggle-counter-${ri}`} label={t("t_counter")} value={!!room.needs_counter_skirting} onChange={(v) => setRoom(ri, "needs_counter_skirting", v)} />
                        <Toggle testId={`toggle-removal-${ri}`} label={t("t_removal")} value={!!room.needs_floor_removal} onChange={(v) => setRoom(ri, "needs_floor_removal", v)} />
                        <Toggle testId={`toggle-leveling-${ri}`} label={t("t_leveling")} value={!!room.needs_floor_leveling} onChange={(v) => setRoom(ri, "needs_floor_leveling", v)} />
                        <Toggle testId={`toggle-protection-${ri}`} label={t("t_protection")} value={!!room.needs_floor_protection} onChange={(v) => setRoom(ri, "needs_floor_protection", v)} />
                        <Toggle testId={`toggle-furniture-${ri}`} label={t("t_furniture")} value={!!room.furniture_to_move} onChange={(v) => setRoom(ri, "furniture_to_move", v)} />
                        {room.floor_covering === "lino" && <Toggle testId={`toggle-hotweld-${ri}`} label={t("t_hot_weld")} value={!!room.needs_hot_weld} onChange={(v) => setRoom(ri, "needs_hot_weld", v)} />}
                        {room.floor_covering === "vinyl_homogene_dalles" && <Toggle testId={`toggle-coldweld-${ri}`} label={t("t_cold_weld")} value={!!room.needs_cold_weld} onChange={(v) => setRoom(ri, "needs_cold_weld", v)} />}
                        <Row>
                          <Field label={t("f_rosettes")} testId={`input-rosettes-${ri}`} value={room.num_rosettes} onChange={(e) => setRoom(ri, "num_rosettes", e.target.value)} placeholder="0" className="mb-0" />
                          <Field label={t("f_thresholds")} testId={`input-thresholds-${ri}`} value={room.num_thresholds} onChange={(e) => setRoom(ri, "num_thresholds", e.target.value)} placeholder="0" className="mb-0" />
                        </Row>
                        <Field label={t("f_radiators")} testId={`input-radiators-${ri}`} value={room.num_radiators} onChange={(e) => setRoom(ri, "num_radiators", e.target.value)} placeholder="0" />
                        <GroupLabel>{t("supply_title")}</GroupLabel>
                        <Field label={t("product_name")} testId={`input-productname-${ri}`} value={room.product_name} onChange={(e) => setRoom(ri, "product_name", e.target.value)} placeholder={t("ph_product")} />
                        <Field label={t("product_price_m2")} testId={`input-productprice-${ri}`} value={room.product_price} onChange={(e) => setRoom(ri, "product_price", e.target.value)} placeholder="45" />
                        <Hint>{t("price_tvac_hint")}</Hint>
                        {room.floor_covering === "lino" && (
                          <Field label={t("weld_reference_label")} testId={`input-weldref-${ri}`} value={room.weld_reference} onChange={(e) => setRoom(ri, "weld_reference", e.target.value)} placeholder={t("ph_weld_reference")} />
                        )}
                      </>
                    )}
                  </>
                ) : category === "curtains" ? (
                  <>
                    <InfoBox>{t("curtains_win_hint")}</InfoBox>
                    {room.windows.map((w, wi) => (
                      <div key={wi} data-testid={`win-card-${ri}-${wi}`} className="mb-3 rounded-xl border border-line bg-paper p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <p className="text-[14px] font-semibold text-ink">{t("window_n")} {wi + 1}</p>
                          <button data-testid={`remove-win-${ri}-${wi}`} onClick={() => removeWindow(ri, wi)}>
                            <Icon name="x" size={16} className="text-err" />
                          </button>
                        </div>
                        <Row>
                          <Field label={t("w_width")} testId={`input-win-w-${ri}-${wi}`} value={w.width} onChange={(e) => setWindow(ri, wi, "width", e.target.value)} placeholder="1.2" />
                          <Field label={t("w_height")} testId={`input-win-h-${ri}-${wi}`} value={w.height} onChange={(e) => setWindow(ri, wi, "height", e.target.value)} placeholder="1.4" />
                        </Row>
                        <Row>
                          <Field label={t("w_total_height")} testId={`input-win-th-${ri}-${wi}`} value={w.total_height || ""} onChange={(e) => setWindow(ri, wi, "total_height", e.target.value)} placeholder="2.7" />
                          <Field label={t("w_sill_height")} testId={`input-win-sh-${ri}-${wi}`} value={w.sill_height || ""} onChange={(e) => setWindow(ri, wi, "sill_height", e.target.value)} placeholder="0.9" />
                        </Row>
                        <Toggle testId={`toggle-voilage-${ri}-${wi}`} label={t("opt_voilage")} value={!!w.want_voilage} onChange={(v) => setWindow(ri, wi, "want_voilage", v)} />
                        {w.want_voilage && <Toggle testId={`toggle-plombe-${ri}-${wi}`} label={t("opt_voilage_plombe")} value={!!w.voilage_plombe} onChange={(v) => setWindow(ri, wi, "voilage_plombe", v)} />}
                        <Toggle testId={`toggle-tenture-${ri}-${wi}`} label={t("opt_tenture")} value={!!w.want_tenture} onChange={(v) => setWindow(ri, wi, "want_tenture", v)} />
                        <GroupLabel>{t("drop_label")}</GroupLabel>
                        <Row>
                          <ChoiceCard testId={`drop-sol-${ri}-${wi}`} label={t("opt_drop_floor")} icon="arrow-down" selected={(w.drop_target || "sol") === "sol"} onClick={() => setWindow(ri, wi, "drop_target", "sol")} />
                          <ChoiceCard testId={`drop-tablette-${ri}-${wi}`} label={t("opt_drop_sill")} icon="align-center" selected={w.drop_target === "tablette"} onClick={() => setWindow(ri, wi, "drop_target", "tablette")} />
                        </Row>
                        <GroupLabel>{t("elements_label")}</GroupLabel>
                        <Row cols={3}>
                          {["1", "2", "3"].map((n) => (
                            <ChoiceCard key={n} testId={`elements-${n}-${ri}-${wi}`} label={n} icon="layers" selected={(w.n_elements || "2") === n} onClick={() => setWindow(ri, wi, "n_elements", n)} />
                          ))}
                        </Row>
                        <GroupLabel>{t("mount_label")}</GroupLabel>
                        <Row cols={3}>
                          <ChoiceCard testId={`mount-cacherail-${ri}-${wi}`} label={t("opt_cacherail")} icon="minus" selected={(w.mount || "cache_rail") === "cache_rail"} onClick={() => setWindow(ri, wi, "mount", "cache_rail")} />
                          <ChoiceCard testId={`mount-plafond-${ri}-${wi}`} label={t("opt_plafond")} icon="chevrons-up" selected={w.mount === "plafond"} onClick={() => setWindow(ri, wi, "mount", "plafond")} />
                          <ChoiceCard testId={`mount-mur-${ri}-${wi}`} label={t("opt_mur")} icon="square" selected={w.mount === "mur"} onClick={() => setWindow(ri, wi, "mount", "mur")} />
                        </Row>
                        {w.mount === "mur" && (
                          <>
                            <GroupLabel>{t("wall_support_label")}</GroupLabel>
                            <Row cols={3}>
                              <ChoiceCard testId={`wsupport-barre-${ri}-${wi}`} label={t("opt_barre")} icon="minus" selected={(w.wall_support || "rail") === "barre"} onClick={() => setWindow(ri, wi, "wall_support", "barre")} />
                              <ChoiceCard testId={`wsupport-rail-${ri}-${wi}`} label={t("opt_rail")} icon="minus" selected={(w.wall_support || "rail") === "rail"} onClick={() => setWindow(ri, wi, "wall_support", "rail")} />
                              <ChoiceCard testId={`wsupport-both-${ri}-${wi}`} label={t("opt_both_rails")} icon="layers" selected={w.wall_support === "both"} onClick={() => setWindow(ri, wi, "wall_support", "both")} />
                            </Row>
                            <Field label={t("ceiling_to_rod")} testId={`input-ctr-${ri}-${wi}`} value={w.ceiling_to_rod || ""} onChange={(e) => setWindow(ri, wi, "ceiling_to_rod", e.target.value)} placeholder="0.15" />
                            <InfoBox>{t("wall_vat_hint")}</InfoBox>
                          </>
                        )}
                      </div>
                    ))}
                    <AddBtn testId={`add-window-${ri}`} onClick={() => addWindow(ri)} label={t("add_window")} />
                  </>
                ) : (
                  <>
                    <Row>
                      <Field label={t("f_height")} testId={`input-height-${ri}`} value={room.wall_height} onChange={(e) => setRoom(ri, "wall_height", e.target.value)} placeholder="2.5" />
                      <Field label={t("f_length")} testId={`input-length-${ri}`} value={room.wall_length} onChange={(e) => setRoom(ri, "wall_length", e.target.value)} placeholder="4" />
                    </Row>
                    <Row>
                      <Field label={t("f_width")} testId={`input-wallwidth-${ri}`} value={room.wall_width} onChange={(e) => setRoom(ri, "wall_width", e.target.value)} placeholder="3" />
                      <Field label={t("f_doors")} testId={`input-doors-${ri}`} value={room.num_doors} onChange={(e) => setRoom(ri, "num_doors", e.target.value)} placeholder="1" />
                    </Row>
                    <GroupLabel>{t("windows_dims_title")}</GroupLabel>
                    {room.windows.map((w, wi) => (
                      <div key={wi} className="mb-2 flex items-end gap-2">
                        <div className="flex-1"><Field label={`${t("window_n")} ${wi + 1} — ${t("w_width")}`} testId={`input-win-w-${ri}-${wi}`} value={w.width} onChange={(e) => setWindow(ri, wi, "width", e.target.value)} placeholder="1.2" className="mb-0" /></div>
                        <div className="flex-1"><Field label={t("w_height")} testId={`input-win-h-${ri}-${wi}`} value={w.height} onChange={(e) => setWindow(ri, wi, "height", e.target.value)} placeholder="1.4" className="mb-0" /></div>
                        <button data-testid={`remove-win-${ri}-${wi}`} onClick={() => removeWindow(ri, wi)} className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg border border-line">
                          <Icon name="x" size={16} className="text-err" />
                        </button>
                      </div>
                    ))}
                    <AddBtn testId={`add-window-${ri}`} onClick={() => addWindow(ri)} label={t("add_window")} />
                    {category === "wallpaper" && (
                      <>
                        <GroupLabel>{t("room_wp_title")}</GroupLabel>
                        <div className="mb-3 grid grid-cols-2 gap-2">
                          {[["normal", "opt_wp_normal", "file"], ["fragile", "opt_wp_fragile", "alert-triangle"], ["poster", "opt_wp_poster", "image"], ["vinyl", "opt_wp_vinyl", "droplet"]].map(([val, key, icon]) => (
                            <ChoiceCard key={val} testId={`choice-wptype-${ri}-${val}`} label={t(key)} icon={icon} selected={(room.wp_type || "normal") === val} onClick={() => setRoom(ri, "wp_type", val)} />
                          ))}
                        </div>
                        <GroupLabel>{t("roll_width")}</GroupLabel>
                        <Row cols={3}>
                          <ChoiceCard testId={`choice-w53-${ri}`} label={t("opt_w53")} icon="minus" selected={(room.wp_roll_width || "0.53") === "0.53"} onClick={() => setRoom(ri, "wp_roll_width", "0.53")} />
                          <ChoiceCard testId={`choice-w70-${ri}`} label={t("opt_w70")} icon="minus" selected={room.wp_roll_width === "0.70"} onClick={() => setRoom(ri, "wp_roll_width", "0.70")} />
                          <ChoiceCard testId={`choice-w90-${ri}`} label={t("opt_w90")} icon="minus" selected={room.wp_roll_width === "0.90"} onClick={() => setRoom(ri, "wp_roll_width", "0.90")} />
                        </Row>
                        <Field label={t("wp_reference")} testId={`input-wpref-${ri}`} value={room.wp_reference} onChange={(e) => setRoom(ri, "wp_reference", e.target.value)} placeholder={t("ph_wp_reference")} className="mb-0" />
                        {form.wp_mode === "mix" && (
                          <div className="mt-4">
                            <GroupLabel>{t("wall_finish_title")}</GroupLabel>
                            <Hint>{t("wall_finish_hint")}</Hint>
                            {[0, 1, 2, 3].map((wi) => {
                              const cur = (room.walls || [])[wi] || "wallpaper";
                              const setWall = (val) => {
                                const arr = [0, 1, 2, 3].map((k) => (room.walls || [])[k] || "wallpaper");
                                arr[wi] = val;
                                setRoom(ri, "walls", arr);
                              };
                              return (
                                <div key={wi} className="mb-2">
                                  <p className="mb-1 text-[13px] font-medium text-ink-3">{t("wall_n")} {wi + 1}</p>
                                  <Row>
                                    <ChoiceCard testId={`wall-${ri}-${wi}-wp`} label={t("opt_wall_wp")} icon="file" selected={cur === "wallpaper"} onClick={() => setWall("wallpaper")} />
                                    <ChoiceCard testId={`wall-${ri}-${wi}-paint`} label={t("opt_wall_paint")} icon="droplet" selected={cur === "paint"} onClick={() => setWall("paint")} />
                                  </Row>
                                </div>
                              );
                            })}
                            <GroupLabel>{t("room_paint_title")}</GroupLabel>
                            <Row cols={3}>
                              <ChoiceCard testId={`pq-standard-${ri}`} label={t("opt_pq_standard")} icon="circle" selected={(room.paint_quality || "standard") === "standard"} onClick={() => setRoom(ri, "paint_quality", "standard")} />
                              <ChoiceCard testId={`pq-premium-${ri}`} label={t("opt_pq_premium")} icon="star" selected={room.paint_quality === "premium"} onClick={() => setRoom(ri, "paint_quality", "premium")} />
                              <ChoiceCard testId={`pq-hautdegamme-${ri}`} label={t("opt_pq_hautdegamme")} icon="award" selected={room.paint_quality === "hautdegamme"} onClick={() => setRoom(ri, "paint_quality", "hautdegamme")} />
                            </Row>
                            <div data-testid={`mix-recap-${ri}`} className="mt-2 flex justify-between rounded-xl bg-paper-2 px-3 py-2 text-[13px]">
                              <span className="text-ink-3">{t("mix_recap_wp")} <b className="text-brand">{wallSplit(room).wpArea} m²</b></span>
                              <span className="text-ink-3">{t("mix_recap_paint")} <b className="text-brand">{wallSplit(room).ptArea} m²</b></span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {category === "painting" && (
                      <>
                        <GroupLabel>{t("room_paint_title")}</GroupLabel>
                        <Row cols={3}>
                          <ChoiceCard testId={`pq-standard-${ri}`} label={t("opt_pq_standard")} icon="circle" selected={(room.paint_quality || "standard") === "standard"} onClick={() => setRoom(ri, "paint_quality", "standard")} />
                          <ChoiceCard testId={`pq-premium-${ri}`} label={t("opt_pq_premium")} icon="star" selected={room.paint_quality === "premium"} onClick={() => setRoom(ri, "paint_quality", "premium")} />
                          <ChoiceCard testId={`pq-hautdegamme-${ri}`} label={t("opt_pq_hautdegamme")} icon="award" selected={room.paint_quality === "hautdegamme"} onClick={() => setRoom(ri, "paint_quality", "hautdegamme")} />
                        </Row>
                        <Hint>{t("paint_quality_hint")}</Hint>
                        <Field label={t("paint_color_label")} testId={`input-paintcolor-${ri}`} value={room.paint_color} onChange={(e) => setRoom(ri, "paint_color", e.target.value)} placeholder={t("ph_paint_color")} className="mb-0" />
                        <GroupLabel>{t("wall_state_title")}</GroupLabel>
                        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <ChoiceCard testId={`ws-good-${ri}`} label={t("opt_ws_good")} icon="check-circle" selected={room.wall_state === "good"} onClick={() => setRoom(ri, "wall_state", room.wall_state === "good" ? "" : "good")} />
                          <ChoiceCard testId={`ws-raw-${ri}`} label={t("opt_ws_raw")} icon="square" selected={room.wall_state === "raw"} onClick={() => setRoom(ri, "wall_state", room.wall_state === "raw" ? "" : "raw")} />
                          <ChoiceCard testId={`ws-stains-${ri}`} label={t("opt_ws_stains")} icon="alert-triangle" selected={room.wall_state === "stains"} onClick={() => setRoom(ri, "wall_state", room.wall_state === "stains" ? "" : "stains")} />
                        </div>
                        {room.wall_state === "stains" && (
                          <>
                            <GroupLabel>{t("finish_type_title")}</GroupLabel>
                            <Row cols={3}>
                              <ChoiceCard testId={`ft-non_lavable-${ri}`} label={t("opt_ft_non_lavable")} icon="droplet" selected={(room.finish_type || "non_lavable") === "non_lavable"} onClick={() => setRoom(ri, "finish_type", "non_lavable")} />
                              <ChoiceCard testId={`ft-lavable-${ri}`} label={t("opt_ft_lavable")} icon="droplet" selected={room.finish_type === "lavable"} onClick={() => setRoom(ri, "finish_type", "lavable")} />
                              <ChoiceCard testId={`ft-lessivable-${ri}`} label={t("opt_ft_lessivable")} icon="droplet" selected={room.finish_type === "lessivable"} onClick={() => setRoom(ri, "finish_type", "lessivable")} />
                            </Row>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
            <button data-testid="add-room" onClick={addRoom} className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand py-3 text-[14px] font-semibold text-brand transition-colors hover:bg-brand-tertiary/40">
              <Icon name="plus-circle" size={18} /> {t("add_room")}
            </button>
          </>
        )}

        {step === "lining" && (
          <>
            <StepHead title={t("confection_title")} sub={t("confection_sub")} />
            <GroupLabel>{t("confection_kind")}</GroupLabel>
            <Row>
              <ChoiceCard testId="choice-tentures" label={t("opt_tentures")} icon="align-justify" selected={form.curtain_kind === "tentures"} onClick={() => set("curtain_kind", "tentures")} />
              <ChoiceCard testId="choice-stores" label={t("opt_stores")} icon="sliders" selected={form.curtain_kind === "stores"} onClick={() => set("curtain_kind", "stores")} />
            </Row>
            {form.curtain_kind === "tentures" && (
              <>
                <GroupLabel>{t("ruflette_label")}</GroupLabel>
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {[["fronces", "opt_ruf_fronces"], ["plis_plats", "opt_ruf_plats"], ["plis_flamands", "opt_ruf_flamands"], ["wave", "opt_ruf_wave"], ["tuyau_orgue", "opt_ruf_tuyau"]].map(([val, key]) => (
                    <ChoiceCard key={val} testId={`choice-ruf-${val}`} label={t(key)} icon="wind" selected={form.ruflette_type === val} onClick={() => set("ruflette_type", val)} />
                  ))}
                </div>
                <GroupLabel>{t("fullness_label")}</GroupLabel>
                <Row cols={3}>
                  {[["2", "x2"], ["2.5", "x2,5"], ["3", "x3"]].map(([val, lbl]) => (
                    <ChoiceCard key={val} testId={`choice-fullness-${val}`} label={lbl} icon="maximize" selected={form.fullness === val} onClick={() => set("fullness", val)} />
                  ))}
                </Row>
                <Hint>{t("fullness_hint")}</Hint>
                <GroupLabel>{t("hook_type_label")}</GroupLabel>
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {[["glisser", "opt_hook_glisser"], ["metal", "opt_hook_metal"], ["plastique", "opt_hook_plastique"], ["pince", "opt_hook_pince"]].map(([val, key]) => (
                    <ChoiceCard key={val} testId={`choice-hook-${val}`} label={t(key)} icon="link" selected={form.hook_type === val} onClick={() => set("hook_type", val)} />
                  ))}
                </div>
                <Toggle testId="toggle-lining" label={t("t_lining")} value={form.needs_lining} onChange={(v) => set("needs_lining", v)} />
                {form.needs_lining && (
                  <>
                    <GroupLabel>{t("lining_type")}</GroupLabel>
                    <Row cols={3}>
                      <ChoiceCard testId="choice-blackout" label={t("opt_blackout")} icon="moon" selected={form.lining_type === "blackout"} onClick={() => set("lining_type", "blackout")} />
                      <ChoiceCard testId="choice-thermal" label={t("opt_thermal")} icon="thermometer" selected={form.lining_type === "thermal"} onClick={() => set("lining_type", "thermal")} />
                      <ChoiceCard testId="choice-both" label={t("opt_both")} icon="layers" selected={form.lining_type === "both"} onClick={() => set("lining_type", "both")} />
                    </Row>
                  </>
                )}
                <Toggle testId="toggle-voile-lined" label={t("t_voile_lined")} value={form.voile_lined} onChange={(v) => set("voile_lined", v)} />
                <Toggle testId="toggle-rails" label={t("t_rails")} value={form.needs_rails} onChange={(v) => set("needs_rails", v)} />
                {form.needs_rails && (
                  <>
                    <GroupLabel>{t("rail_kind_label")}</GroupLabel>
                    <Row>
                      <ChoiceCard testId="choice-rail-rail" label={t("opt_rail")} icon="minus" selected={form.rail_kind === "rail"} onClick={() => set("rail_kind", "rail")} />
                      <ChoiceCard testId="choice-rail-barre" label={t("opt_barre")} icon="maximize-2" selected={form.rail_kind === "barre"} onClick={() => set("rail_kind", "barre")} />
                    </Row>
                    <GroupLabel>{t("rail_vat_label")}</GroupLabel>
                    <Row>
                      <ChoiceCard testId="choice-vat6" label={t("opt_vat6")} icon="percent" selected={!form.rail_vat21} onClick={() => set("rail_vat21", false)} />
                      <ChoiceCard testId="choice-vat21" label={t("opt_vat21")} icon="percent" selected={form.rail_vat21} onClick={() => set("rail_vat21", true)} />
                    </Row>
                    <Hint>{t("rail_vat_hint")}</Hint>
                  </>
                )}
              </>
            )}
            {form.curtain_kind === "stores" && (
              <>
                <GroupLabel>{t("store_family_label")}</GroupLabel>
                <Row>
                  <ChoiceCard testId="choice-confectionne" label={t("opt_confectionne")} icon="scissors" selected={form.store_family === "confectionne"} onClick={() => { set("store_family", "confectionne"); set("store_type", ""); }} />
                  <ChoiceCard testId="choice-industriel" label={t("opt_industriel")} icon="settings" selected={form.store_family === "industriel"} onClick={() => { set("store_family", "industriel"); set("store_type", ""); }} />
                </Row>
                {form.store_family === "confectionne" && (
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    {[["bateau", "opt_st_bateau"], ["baguettes", "opt_st_baguettes"]].map(([val, key]) => (
                      <ChoiceCard key={val} testId={`choice-st-${val}`} label={t(key)} icon="square" selected={form.store_type === val} onClick={() => set("store_type", val)} />
                    ))}
                  </div>
                )}
                {form.store_family === "industriel" && (
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    {[["lamelles_verticales", "opt_st_lamelles"], ["venitien", "opt_st_venitien"], ["enrouleur", "opt_st_enrouleur"], ["plisse", "opt_st_plisse"]].map(([val, key]) => (
                      <ChoiceCard key={val} testId={`choice-st-${val}`} label={t(key)} icon="square" selected={form.store_type === val} onClick={() => set("store_type", val)} />
                    ))}
                  </div>
                )}
                {form.store_type === "plisse" && (
                  <>
                    <GroupLabel>{t("plisse_variant_label")}</GroupLabel>
                    <Row>
                      <ChoiceCard testId="choice-plisse-simple" label={t("opt_simple")} icon="minus" selected={form.plisse_variant === "simple"} onClick={() => set("plisse_variant", "simple")} />
                      <ChoiceCard testId="choice-plisse-double" label={t("opt_double")} icon="copy" selected={form.plisse_variant === "double"} onClick={() => set("plisse_variant", "double")} />
                    </Row>
                    {form.plisse_variant === "double" && (
                      <>
                        <Toggle testId="toggle-opaque" label={t("t_opaque")} value={form.plisse_opaque} onChange={(v) => set("plisse_opaque", v)} />
                        <Toggle testId="toggle-thermique" label={t("t_thermique")} value={form.plisse_thermique} onChange={(v) => set("plisse_thermique", v)} />
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {step === "condition" && (
          <>
            <StepHead title={t("cond_title")} sub={t("cond_sub")} />
            {category === "painting" && (
              <>
                <GroupLabel>{t("location")}</GroupLabel>
                <Row>
                  <ChoiceCard testId="choice-interior" label={t("opt_interior")} icon="home" selected={form.paint_location === "interior"} onClick={() => set("paint_location", "interior")} />
                  <ChoiceCard testId="choice-exterior" label={t("opt_exterior")} icon="sun" selected={form.paint_location === "exterior"} onClick={() => set("paint_location", "exterior")} />
                </Row>
                <GroupLabel>{t("paint_elements")}</GroupLabel>
                <Toggle testId="toggle-pwalls" label={t("e_walls")} value={form.paint_walls} onChange={(v) => set("paint_walls", v)} />
                <Toggle testId="toggle-pplinthes" label={t("e_plinthes")} value={form.paint_plinthes} onChange={(v) => set("paint_plinthes", v)} />
                <Toggle testId="toggle-pportes" label={t("e_portes")} value={form.paint_portes} onChange={(v) => set("paint_portes", v)} />
                {form.paint_portes && (
                  <div className="mb-3 rounded-xl border border-line bg-white p-3">
                    <Field label={t("f_doors_count")} testId="input-doors-count" value={form.paint_doors_count} onChange={(e) => set("paint_doors_count", e.target.value)} placeholder="3" />
                    <GroupLabel>{t("finish_label")}</GroupLabel>
                    <Row>
                      <ChoiceCard testId="door-synth" label={t("opt_finish_synth")} icon="droplet" selected={form.door_finish === "synthetic"} onClick={() => set("door_finish", "synthetic")} />
                      <ChoiceCard testId="door-aqua" label={t("opt_finish_aqua")} icon="droplet" selected={form.door_finish === "aqueous"} onClick={() => set("door_finish", "aqueous")} />
                    </Row>
                  </div>
                )}
                <Toggle testId="toggle-pchassis" label={t("e_chassis")} value={form.paint_chassis} onChange={(v) => set("paint_chassis", v)} />
                <Toggle testId="toggle-pcorniches" label={t("e_corniches")} value={form.paint_corniches} onChange={(v) => set("paint_corniches", v)} />
                <GroupLabel>{t("paint_access")}</GroupLabel>
                <Hint>{t("paint_access_sub")}</Hint>
                <Toggle testId="toggle-tarps" label={t("a_tarps")} value={form.needs_tarps} onChange={(v) => set("needs_tarps", v)} />
                <Toggle testId="toggle-masking" label={t("a_masking")} value={form.needs_masking} onChange={(v) => set("needs_masking", v)} />
                <Toggle testId="toggle-scaffold" label={t("a_scaffold")} value={form.needs_scaffolding} onChange={(v) => set("needs_scaffolding", v)} />
                <Toggle testId="toggle-ladder" label={t("a_ladder")} value={form.needs_ladder} onChange={(v) => set("needs_ladder", v)} />
              </>
            )}
            <Toggle testId="toggle-dewallpaper" label={t("t_dewallpaper")} value={form.needs_dewallpapering} onChange={(v) => set("needs_dewallpapering", v)} />
            <Toggle testId="toggle-smoothing" label={t("t_smoothing")} value={form.needs_smoothing} onChange={(v) => set("needs_smoothing", v)} />
            <Toggle testId="toggle-repairs" label={t("t_repairs")} value={form.needs_repairs} onChange={(v) => set("needs_repairs", v)} />
            {category === "painting" && (
              <>
                <Toggle testId="toggle-nonwoven" label={t("t_nonwoven")} value={form.needs_nonwoven} onChange={(v) => set("needs_nonwoven", v)} />
                <Toggle testId="toggle-moldings" label={t("t_moldings")} value={form.needs_moldings} onChange={(v) => { set("needs_moldings", v); if (v && !form.molding_action) set("molding_action", "pose"); }} />
                {form.needs_moldings && (
                  <>
                    <GroupLabel>{t("molding_action_label")}</GroupLabel>
                    <Row cols={3}>
                      <ChoiceCard testId="mold-act-pose" label={t("opt_mold_pose")} icon="tool" selected={(form.molding_action || "pose") === "pose"} onClick={() => set("molding_action", "pose")} />
                      <ChoiceCard testId="mold-act-pose-paint" label={t("opt_mold_pose_paint")} icon="brush" selected={form.molding_action === "pose_paint"} onClick={() => set("molding_action", "pose_paint")} />
                      <ChoiceCard testId="mold-act-paint" label={t("opt_mold_paint_existing")} icon="paintbrush" selected={form.molding_action === "paint_existing"} onClick={() => set("molding_action", "paint_existing")} />
                    </Row>
                    {(form.molding_action || "pose") !== "paint_existing" && (
                      <Row>
                        <ChoiceCard testId="mold-expansee" label={t("opt_mold_expansee")} icon="box" selected={form.molding_type === "expansee"} onClick={() => set("molding_type", "expansee")} />
                        <ChoiceCard testId="mold-extrudee" label={t("opt_mold_extrudee")} icon="box" selected={form.molding_type === "extrudee"} onClick={() => set("molding_type", "extrudee")} />
                      </Row>
                    )}
                  </>
                )}
                {form.paint_location === "interior" && (
                  <Field label={t("f_radiators")} testId="input-radiators" value={form.num_radiators} onChange={(e) => set("num_radiators", e.target.value)} placeholder="0" />
                )}
                <Toggle testId="toggle-floorpaint" label={t("t_floor_paint")} value={form.floor_paint} onChange={(v) => set("floor_paint", v)} />
                {form.floor_paint && (
                  <div className="mb-3 rounded-xl border border-line bg-white p-3">
                    <GroupLabel>{t("floor_paint_type_label")}</GroupLabel>
                    <Row cols={3}>
                      {[["pliolite", "opt_fp_pliolite"], ["polyurethane", "opt_fp_pu"], ["epoxy", "opt_fp_epoxy"]].map(([v, k]) => (
                        <ChoiceCard key={v} testId={`fp-${v}`} label={t(k)} icon="layers" selected={form.floor_paint_type === v} onClick={() => set("floor_paint_type", v)} />
                      ))}
                    </Row>
                    <GroupLabel>{t("finish_label")}</GroupLabel>
                    <Row>
                      <ChoiceCard testId="fp-synth" label={t("opt_finish_synth")} icon="droplet" selected={form.floor_paint_finish === "synthetic"} onClick={() => set("floor_paint_finish", "synthetic")} />
                      <ChoiceCard testId="fp-aqua" label={t("opt_finish_aqua")} icon="droplet" selected={form.floor_paint_finish === "aqueous"} onClick={() => set("floor_paint_finish", "aqueous")} />
                    </Row>
                    <Field label={t("f_floor_paint_area")} testId="input-floorpaint-area" value={form.floor_paint_area} onChange={(e) => set("floor_paint_area", e.target.value)} placeholder="25" />
                    <Hint>{t("floor_paint_hint")}</Hint>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {step === "flooring" && (
          <>
            <StepHead title={t("floor_title")} sub={t("floor_sub")} />
            <Toggle testId="toggle-hasown" label={t("t_has_own")} value={form.has_own_material} onChange={(v) => set("has_own_material", v)} />
            {form.has_own_material && (
              <div data-testid="own-warn" className="mb-4 flex items-start gap-3 rounded-xl bg-[#F7E9E6] p-4">
                <Icon name="alert-triangle" size={16} className="mt-0.5 shrink-0 text-err" />
                <span className="text-[13px] leading-relaxed text-err">{t("own_warn")}</span>
              </div>
            )}
          </>
        )}

        {step === "supply" && (
          <>
            <StepHead title={t("supply_title")} sub={t("supply_sub")} />
            <Toggle testId="toggle-hasown" label={t("t_has_own")} value={form.has_own_material} onChange={(v) => set("has_own_material", v)} />
            {form.has_own_material && (
              <div data-testid="own-warn" className="mb-4 flex items-start gap-3 rounded-xl bg-[#F7E9E6] p-4">
                <Icon name="alert-triangle" size={16} className="mt-0.5 shrink-0 text-err" />
                <span className="text-[13px] leading-relaxed text-err">{t("own_warn")}</span>
              </div>
            )}

            {category === "wallpaper" && !form.has_own_material && (
              <>
                <InfoBox>{t("wp_type_hint")}</InfoBox>
                <GroupLabel>{t("sold_by")}</GroupLabel>
                <Row>
                  <ChoiceCard testId="choice-soldroll" label={t("opt_roll")} icon="package" selected={form.wp_sold_by === "roll"} onClick={() => set("wp_sold_by", "roll")} />
                  <ChoiceCard testId="choice-soldmeter" label={t("opt_meter")} icon="maximize-2" selected={form.wp_sold_by === "meter"} onClick={() => set("wp_sold_by", "meter")} />
                </Row>
                {form.wp_sold_by === "roll" && (
                  <Field label={t("roll_length")} testId="input-rolllen" value={form.wp_roll_length} onChange={(e) => set("wp_roll_length", e.target.value)} placeholder="10.05" />
                )}
                <GroupLabel>{t("raccord")}</GroupLabel>
                <Row cols={3}>
                  <ChoiceCard testId="choice-rnone" label={t("opt_none")} icon="x" selected={form.wp_repeat_type === "none"} onClick={() => set("wp_repeat_type", "none")} />
                  <ChoiceCard testId="choice-rstraight" label={t("opt_straight")} icon="align-justify" selected={form.wp_repeat_type === "straight"} onClick={() => set("wp_repeat_type", "straight")} />
                  <ChoiceCard testId="choice-roffset" label={t("opt_offset")} icon="shuffle" selected={form.wp_repeat_type === "offset"} onClick={() => set("wp_repeat_type", "offset")} />
                </Row>
                {form.wp_repeat_type !== "none" && (
                  <Field label={t("raccord_value")} testId="input-raccord" value={form.wp_repeat} onChange={(e) => set("wp_repeat", e.target.value)} placeholder="32" />
                )}
                <Toggle testId="toggle-ligot" label={t("wp_ligot")} value={form.wp_from_ligot} onChange={(v) => set("wp_from_ligot", v)} />
                {form.wp_from_ligot && (
                  <>
                    <Field label={form.wp_sold_by === "roll" ? t("wp_price_roll") : t("wp_price_meter")} testId="input-wpprice" value={form.wp_price} onChange={(e) => set("wp_price", e.target.value)} placeholder="80" />
                    <Hint>{t("price_tvac_hint")}</Hint>
                  </>
                )}
              </>
            )}

            {category === "curtains" && !form.has_own_material && (
              <>
                <Field label={t("product_name")} testId="input-productname" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} placeholder={t("ph_product")} />
                <Field label={t("product_price_m")} testId="input-productprice" value={form.product_price} onChange={(e) => set("product_price", e.target.value)} placeholder="45" />
                <Hint>{t("price_tvac_hint")}</Hint>
                {category === "curtains" && (
                  <>
                    <Field label={t("fabric_width")} testId="input-fabricwidth" value={form.product_width} onChange={(e) => set("product_width", e.target.value)} placeholder="1.4" />
                    <Field label={t("voile_width")} testId="input-voilewidth" value={form.voile_width} onChange={(e) => set("voile_width", e.target.value)} placeholder="3.0" />
                    <GroupLabel>{t("raccord")}</GroupLabel>
                    <Row cols={3}>
                      <ChoiceCard testId="choice-crnone" label={t("opt_none")} icon="x" selected={form.curtain_repeat_type === "none"} onClick={() => set("curtain_repeat_type", "none")} />
                      <ChoiceCard testId="choice-crstraight" label={t("opt_straight")} icon="align-justify" selected={form.curtain_repeat_type === "straight"} onClick={() => set("curtain_repeat_type", "straight")} />
                      <ChoiceCard testId="choice-croffset" label={t("opt_offset")} icon="shuffle" selected={form.curtain_repeat_type === "offset"} onClick={() => set("curtain_repeat_type", "offset")} />
                    </Row>
                    <Hint>{t("curtain_raccord_hint")}</Hint>
                    {form.curtain_repeat_type !== "none" && (
                      <Field label={t("raccord_value")} testId="input-craccord" value={form.curtain_repeat} onChange={(e) => set("curtain_repeat", e.target.value)} placeholder="32" />
                    )}
                  </>
                )}
                {category === "flooring" && form.rooms.some((r) => r.floor_covering === "lino") && (
                  <Field label={t("weld_reference_label")} testId="input-weldref" value={form.weld_reference} onChange={(e) => set("weld_reference", e.target.value)} placeholder={t("ph_weld_reference")} />
                )}
              </>
            )}
          </>
        )}

        {step === "photos" && (
          <>
            <StepHead title={t("photos_title")} sub={t("photos_sub")} />
            <div className="mb-4 flex flex-wrap gap-3">
              {form.photos.map((p, i) => (
                <div key={i} className="relative h-24 w-24 overflow-hidden rounded-xl border border-line">
                  <img src={p} alt="" className="h-full w-full object-cover" />
                  <button data-testid={`remove-photo-${i}`} onClick={() => set("photos", form.photos.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60">
                    <Icon name="x" size={14} className="text-white" />
                  </button>
                </div>
              ))}
              <label data-testid="add-photo" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line-2 text-mute transition-colors hover:border-brand hover:text-brand">
                <Icon name="upload" size={22} />
                <span className="text-xs">{t("add_photo")}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhoto} />
              </label>
            </div>
            <Field label={t("f_notes")} testId="input-notes" multiline value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("ph_notes")} />
            <button data-testid="gdpr-consent" onClick={() => set("gdpr_consent", !form.gdpr_consent)} className="flex items-start gap-3 text-left">
              <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border", form.gdpr_consent ? "border-brand bg-brand" : "border-line-2 bg-white")}>
                {form.gdpr_consent && <Icon name="check" size={14} className="text-white" />}
              </span>
              <span className="text-[13px] leading-relaxed text-ink-3">{t("gdpr_label")}</span>
            </button>
            <LegalLinks pro={pro} align="left" className="mt-3" />
          </>
        )}

        {error ? <p data-testid="wizard-error" className="mt-4 text-[14px] font-medium text-err">{error}</p> : null}
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 border-t border-divider bg-white/90 backdrop-blur-md">
        <div className="mx-auto max-w-2xl px-5 py-3">
          <PrimaryButton
            testId="wizard-next"
            title={stepIdx === steps.length - 1 ? t("cta_estimate") : t("cta_continue")}
            icon={stepIdx === steps.length - 1 ? "zap" : "arrow-right"}
            onClick={next}
            loading={submitting}
          />
          <ProFooter pro={pro} className="mt-2" />
        </div>
      </div>
    </div>
  );
}

const StepHead = ({ title, sub }) => (
  <div className="mb-6">
    <h2 className="font-display text-3xl font-semibold text-ink">{title}</h2>
    <p className="mt-1 text-[14px] text-mute">{sub}</p>
  </div>
);

const AddBtn = ({ testId, onClick, label }) => (
  <button data-testid={testId} onClick={onClick} className="flex items-center gap-1.5 text-[14px] font-semibold text-brand">
    <Icon name="plus" size={15} /> {label}
  </button>
);
